import React, { FC, memo, ReactNode, useEffect, useState } from 'react';
import { Menu } from 'antd';
import { useLocation } from 'react-router';
import { reduce } from 'lodash';
import { Link } from 'react-router-dom';
import i18next from 'i18next';

import { NavbarGroupItemTypes } from '../../../types';
import { navigationConfig } from '../../../commons/constants';

const { SubMenu, Item, Divider } = Menu;

const LayoutNavbarMenuContainer: FC = () => {
  const location = useLocation();

  // selected menu item
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [selectedMenuItem, setSelectedMenuItem] = useState<NavbarGroupItemTypes>();
  // selected open menu group items
  const [openKeys, setOpenKeys] = useState<string[]>(['']);
  const [pathName, setPathName] = useState(location.pathname);

  // get current path on menu
  const getPath = (
    data: NavbarGroupItemTypes[],
    url: string,
    parents: NavbarGroupItemTypes[] = []
  ): NavbarGroupItemTypes[] => {
    const items = reduce(
      data,
      (result: NavbarGroupItemTypes[], entry: NavbarGroupItemTypes) => {
        if (result.length) return result;

        if (entry.url === url || entry.key === url) {
          return [entry].concat(parents);
        }

        if (entry.children) {
          const nested = getPath(entry.children, url, [entry].concat(parents));
          return nested || result;
        }

        return result;
      },
      []
    );

    return items.length > 0 ? items : [];
  };

  // get current active menu
  const getActiveMenuItem = () => {
    if (!pathName) return;

    // eslint-disable-next-line prefer-const
    let [activeMenuItem, ...path] = getPath(navigationConfig, pathName);

    setSelectedKey(activeMenuItem ? activeMenuItem.key : '');
    setSelectedMenuItem(activeMenuItem);
    setOpenKeys(activeMenuItem ? path.map((entry) => entry.key) : []);
  };

  useEffect(() => {
    setPathName(location.pathname);
  }, [location]);

  useEffect(() => {
    if (!pathName) return;
    getActiveMenuItem();
  }, [pathName]);

  // handle click on menu item
  const handleClick = ({ key }) => {
    // action change routing when click on menu item
    if (selectedKey !== key) {
      setSelectedKey(key);
    }
  };

  // handle event change open menu group items
  const onOpenChange = (changedOpenKeys: string[]) => {
    setOpenKeys(changedOpenKeys);
  };

  const generateMenuItem = (item: NavbarGroupItemTypes): ReactNode => {
    if (item.type !== 'item' || item.hidden) return null;

    return (
      <Item key={item.key} className="menu__item" icon={<i className={item.icon} />}>
        {item.url &&
        (item.key === selectedKey || (selectedMenuItem && pathName !== selectedMenuItem.url)) ? (
          i18next.t(item.title)
        ) : (
          <Link to={item.url} onClick={undefined}>
            {i18next.t(item.title)}
          </Link>
        )}
      </Item>
    );
  };

  const generateMenuPartitions = (): ReactNode =>
    navigationConfig.map((groupItem) => {
      if (!groupItem || groupItem.hidden) return null;

      switch (groupItem.type) {
        case 'group':
          if (!groupItem.children || !groupItem.children.length) return null;

          return (
            <React.Fragment key={groupItem.key}>
              <Item key={groupItem.key} disabled className="menu__group-item">
                {i18next.t(groupItem.title)}
              </Item>
              {groupItem.children.map((collapseItem) => {
                if (!collapseItem || collapseItem.hidden) return null;

                switch (collapseItem.type) {
                  case 'item':
                    return generateMenuItem(collapseItem);

                  case 'collapse':
                    if (!collapseItem.children || !collapseItem.children.length) return null;

                    return (
                      <SubMenu
                        key={collapseItem.key}
                        className="menu__collapse-item"
                        title={i18next.t(collapseItem.title)}
                        icon={<i className={collapseItem.icon} />}
                      >
                        {collapseItem.children.map((item) => {
                          if (!item) return null;

                          switch (item.type) {
                            case 'item':
                              return generateMenuItem(item);

                            default:
                              return null;
                          }
                        })}
                      </SubMenu>
                    );

                  default:
                    return null;
                }
              })}
            </React.Fragment>
          );

        case 'divider':
          return <Divider key={groupItem.key} className="menu__divider" />;

        default:
          return null;
      }
    });

  return (
    <Menu
      className="menu__container-wrapper"
      onClick={handleClick}
      selectedKeys={[selectedKey]}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      mode="inline"
    >
      {generateMenuPartitions()}
    </Menu>
  );
};

const LayoutNavbarMenuContainerMemo = memo(LayoutNavbarMenuContainer);
export { LayoutNavbarMenuContainerMemo as LayoutNavbarMenuContainer };
